import React, { useState, useEffect } from 'react';

import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { 
  TextField, 
  InputAdornment, 
  Typography, 
  MenuItem,
  CircularProgress,
  Dialog, 
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  Checkbox,
} from '@mui/material';
import Menu from '@mui/material/Menu';
import Chip from '@mui/material/Chip';
import CheckIcon from '@mui/icons-material/Check';
import PeopleIcon from '@mui/icons-material/People';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CancelIcon from '@mui/icons-material/Cancel';
import CreateIcon from '@mui/icons-material/Create';
import Search from '@mui/icons-material/Search';
import AudienceFormDialog from './AudienceFormDialog';
import AudienceFileFormDialog from './AudienceFileFormDialog';
import { AUDIENCE_GET_ENDPOINT, FILE_UPLOAD_AUDIENCE_ENDPOINT, AUDIENCE_EXPORT_ENDPOINT } from '../../config/urls';
import { useAuth } from '../../hooks/AuthProvider';

const DEFAULT_COLUMNS = [
  { id: 'select', label: 'Select' },
  { id: 'name', label: 'Name' }, // Combined First Name and Last Name
  { id: 'phone_no', label: 'Phone No' },
  { id: 'age', label: 'Age' },
  { id: 'gender', label: 'Gender' },
  { id: 'city', label: 'City' },
  { id: 'segments', label: 'Segment' },
  { id: 'email', label: 'Email' },
  { id: 'opt_in', label: 'Opted In' },
  { id: '', label: '' },
];

const tenantSpecificColumns = {
  "oneTouchIndiaMarketingLive" : [
    {id: 'pharmacy', label: "Pharmacy"}
  ]
}

export default function AudienceTable({ refreshKey, updateRefreshKey }) {
  const { getValidToken } = useAuth();
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);
  const [rowCount, setRowCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [charCount, setCharCount] = useState(0);
  const [audienceData, setAudienceData] = useState([]);
  const [audienceModalState, setAudienceModalState] = useState({ isOpen: false, selectedClient: null, mode: 'add', jsonData: '' });
  const [audienceFileModalState, setAudienceFileModalState] = useState({ isOpen: false, mode: 'add', jsonData: '' });
  const URL = process.env.NODE_ENV === 'production' ? 'api' : `http://localhost:8000/api`;
  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: 20
  });
  const [allSegments, setAllSegments] = useState([]);
  const [activeSegments, setActiveSegments] = useState([]);
  const [allSegmentsDetails, setAllSegmentsDetails] = useState([]);
  const [allActiveSegmentsDetails, setAllActiveSegmentsDetails] = useState([]);
  const [segmentFilter, setSegmentFilter] = useState('');
  const [segmentAnchorEl, setSegmentAnchorEl] = useState(null);
  const [showAllErrors, setShowAllErrors] = useState(false);

  const tenant = localStorage.getItem('tenant');

  const sampleFileURL = tenant === "oneTouchIndiaMarketingLive" ? "/sample_onetouch.csv" : "/sample.csv"; // File should be inside `public/sample.pdf`
  const userRole = localStorage.getItem('role');

  const [genderFilter, setGenderFilter] = useState('');
  const [genderAnchorEl, setGenderAnchorEl] = useState(null);
  const [ageFilter, setAgeFilter] = useState('');
  const [ageAnchorEl, setAgeAnchorEl] = useState(null);
  const [cityFilter, setCityFilter] = useState('');
  const [cityAnchorEl, setCityAnchorEl] = useState(null);
  const [allAgeOptions, setAllAgeOptions] = useState([]);
  const [allCityOptions, setAllCityOptions] = useState([]);

  const audienceAddPermission = "audience.add";
  const audienceEditPermission = "audience.change";
  const permissions = localStorage.getItem("permissions") || [];

  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);

  // ADD THESE HANDLER FUNCTIONS after existing handler functions (around line 300-400)
  const handleActionsClick = (event) => {
    setActionsAnchorEl(event.currentTarget);
  };

  const handleActionsClose = () => {
    setActionsAnchorEl(null);
  };

  const handleExportFromMenu = () => {
    handleExport();
    handleActionsClose();
  };

  const handleUploadFromMenu = () => {
    handleOpen();
    handleActionsClose();
  };

  const handleEditSegmentFromMenu = () => {
    handleEditSegmentButton();
    handleActionsClose();
  };

  // const hasReadPermission = permissions.includes(usersReadPermission);
  const hasAddPermission = permissions.includes(audienceAddPermission);
  const hasEditPermission = permissions.includes(audienceEditPermission);
  // const canAccessAI = hasReadPermission && hasAccessPermission;

  const [allZonesDetails, setAllZonesDetails] = useState([]);

  const [open, setOpen] = useState(false);
  // const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const [selectedRecords, setSelectedRecords] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0]; // Only allow a single file
    if (selectedFile) {
      // check if file is .csv
      if (!selectedFile.name.endsWith('.csv')) {
        console.log("Not a CSV");
        setError({ summary: 'Please upload a CSV file', details: [] });
        setFile(null);
        return;
      }
      setFile(selectedFile);
      // Clear any previous error when a valid file is selected
      setError(null);
    }
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setError(null);
  };

  const updateSearchValue = (value) => {
    if (value.length > 50) {
      return;
    }
    setSearchQuery(value);
    setCharCount(value.length);
    setController({
      page: 0,
      rowsPerPage: 20
    });
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      // Select all records that are currently displayed (respecting filters)
      const newSelectedRecords = audienceData.map(audience => audience.id);
      setSelectedRecords(newSelectedRecords);
      setSelectAll(true);
    } else {
      // Deselect all records
      setSelectedRecords([]);
      setSelectAll(false);
    }
  };

  const handleSelectRecord = (event, id) => {
    const selectedIndex = selectedRecords.indexOf(id);
    let newSelected = [];
  
    if (selectedIndex === -1) {
      // Add to selection
      newSelected = [...selectedRecords, id];
    } else {
      // Remove from selection
      newSelected = selectedRecords.filter(recordId => recordId !== id);
    }
  
    setSelectedRecords(newSelected);
    // Update selectAll checkbox state based on whether all visible rows are selected
    setSelectAll(newSelected.length === audienceData.length);
  };

  const isRecordSelected = id => selectedRecords.indexOf(id) !== -1;

  const handleUpload = async () => {
    if (!file) return;
  
    if (file.type !== "text/csv" && file.type !== "application/vnd.ms-excel") {
      setError({ summary: "Invalid file type. Please upload a CSV file.", details: [] });
      return;
    }
  
    if (file.size > 10 * 1024 * 1024) {
      setError({ summary: "File size exceeds 10MB. Please upload a smaller file.", details: [] });
      return;
    }
  
    setLoading(true);
    setError(null);
  
    try {
      const formData = new FormData();
      formData.append("file", file);
      const tenant = localStorage.getItem("tenant");
      const user = JSON.parse(localStorage.getItem("user")); // Parse JSON string into an object
      if (user && user.id) {
          formData.append("user", user.id);
      } else {
          console.error("User ID not found in localStorage.");
      }
        
      const response = await fetch(FILE_UPLOAD_AUDIENCE_ENDPOINT, {
        method: "POST",
        body: formData,
        headers: {
          "X-Tenant-ID": tenant,
        },
      });
  
      const data = await response.json(); // Convert response to JSON
  
      if (!response.ok) {
        if (data.errors && data.errors.file) {
          const errorSummary = data.errors.file.error_summary || "File upload failed due to validation errors.";
          const errorDetails = data.errors.file.errors || [];
          throw { summary: errorSummary, details: errorDetails };
        } else {
          throw { summary: "File upload failed. Please try again.", details: [] };
        }
      }
  
      // Reset state on success
      fetchData();
      
      setFile(null);
      setError(null);
      handleClose();
    } catch (err) {
      setError({ summary: err.summary || "An error occurred.", details: err.details || [] });
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem('tenant');
      
      // Construct query parameters for filtering
      const queryParams = new URLSearchParams({
        search_query: searchQuery,
        segment_filter: segmentFilter,
        age_filter: ageFilter,
        city_filter: cityFilter
      });

      if (genderFilter) {
        // Convert 'Null' to empty string for API filtering
        const genderValue = genderFilter === 'Null' ? '' : genderFilter;
        queryParams.append('gender_filter', genderValue);
      }
  
      console.log("export URL: ", `${AUDIENCE_EXPORT_ENDPOINT}?${queryParams}`);
  
      // Make API request to export endpoint
      const response = await fetch(`${AUDIENCE_EXPORT_ENDPOINT}?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          "X-Tenant-Id": tenant
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to export data');
      }
      
      // Create a blob from the response
      const blob = await response.blob();
      
      // Create a link element to download the file
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      
      // Use the current date for the filename
      const today = new Date();
      const dateStr = today.toISOString().split('T')[0];
      a.download = `audience-data-${dateStr}.csv`;
      
      // Append to the document and trigger download
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting data:', error.message);
      // Add proper error handling/notification here
    }
  };  
  

  const segmentOptions = [...new Set(allSegments)].map(segment => (
    <MenuItem 
      key={segment} 
      value={segment}
    >
      {segment}
    </MenuItem>
  ));

  const handleCreateSegmentButton = () => {
    setAudienceFileModalState({ isOpen: true, mode: 'add', jsonData: '' });
  };

  const handleEditSegmentButton = (audience) => {
    // setAudienceModalState({ isOpen: true, selectedClient: null, mode: 'edit_segment', jsonData: '' });
    
    // When clicking Edit Segment, we'll use the update_customer_segments mode
    setAudienceModalState({ isOpen: true, selectedClient: null, mode: 'update_customer_segments', jsonData: '' });
  };

  const handleReset = () => {
    setSearchQuery('');
    setCharCount(0);
    setSegmentFilter('');
    setGenderFilter('');
    setAgeFilter('');
    setCityFilter('');
    setController({
      page: 0,
      rowsPerPage: 20
    });
  };

  const handleSegmentClick = (event) => {
    setSegmentAnchorEl(event.currentTarget);
  };

  const handleSegmentSelect = (segment) => {
    setSegmentFilter(segment);
    setSegmentAnchorEl(null); // Close the menu after selection
    setController({
      page: 0,
      rowsPerPage: 20
    });
  };

  const handleGenderClick = (event) => {
    setGenderAnchorEl(event.currentTarget);
  };

  const handleGenderSelect = (gender) => {
    const filterValue = gender === 'Null' ? '' : gender;
    setGenderFilter(gender);
    setGenderAnchorEl(null); // Close the menu after selection
    setController({
      page: 0,
      rowsPerPage: 20
    });
  };

  const handleAgeClick = (event) => {
    setAgeAnchorEl(event.currentTarget);
  };

  const handleAgeSelect = (age) => {
    setAgeFilter(age);
    setAgeAnchorEl(null); // Close the menu after selection
    setController({
      page: 0,
      rowsPerPage: 20
    });
  };

  const handleCityClick = (event) => {
    setCityAnchorEl(event.currentTarget);
  };

  const handleCitySelect = (city) => {
    setCityFilter(city);
    setCityAnchorEl(null); // Close the menu after selection
    setController({
      page: 0,
      rowsPerPage: 20
    });
  };

  const fetchData = async () => {
    let searchDateURL = '';

    // Construct query parameters for server-side filtering and pagination
    const queryParams = new URLSearchParams({
      limit: controller.rowsPerPage.toString(),
      offset: (controller.serverSidePage * controller.rowsPerPage).toString(),
      search_query: searchQuery,
      segment_filter: segmentFilter,
      age_filter: ageFilter,
      city_filter: cityFilter
    });

    if (genderFilter) {
      // Convert 'Null' to empty string for API filtering
      const genderValue = genderFilter === 'Null' ? '' : genderFilter;
      queryParams.append('gender_filter', genderValue);
    }

    const token = await getValidToken();
    const tenant = localStorage.getItem('tenant');

    try {
      const response = await fetch(`${AUDIENCE_GET_ENDPOINT}?${queryParams}`, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant
        }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }
      const jsonData = await response.json();

      console.log("GET API response: ", jsonData);

      setAudienceData(jsonData.results);
      setRowCount(jsonData.count);

      setAllSegments(jsonData.all_segments_details.map(segment => segment.segment_name));
      setActiveSegments(jsonData.all_segments_details.filter(segment => segment.active === true).map(segment => segment.segment_name));
      setAllSegmentsDetails(jsonData.all_segments_details);
      setAllActiveSegmentsDetails(jsonData.all_segments_details.filter(segment => segment.active === true));

      setAllZonesDetails(jsonData.all_zones_details);

      setAllAgeOptions(jsonData.age_options || []);
      setAllCityOptions(jsonData.city_options || []);
      
    } catch (error) {
      console.error('Error fetching data:', error.message);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, [
    controller.serverSidePage, 
    controller.rowsPerPage, 
    refreshKey,
    searchQuery,
    segmentFilter,
    genderFilter,
    ageFilter,
    cityFilter
  ]);

  // pagination
  const handlePageChange = (event, newPage) => {
    setController(prev => ({
      ...prev,
      page: newPage,
      serverSidePage: newPage  // Sync server-side page with UI page
    }));
  };

  const handleChangeRowsPerPage = (event) => {
    setController({
      ...prev => ({
        ...prev,
        rowsPerPage: parseInt(event.target.value, 10),
        page: 0,
        serverSidePage: 0  // Reset to first page
      })
    });
  };

  const handleEditClick = (audience) => {
    setAudienceModalState({ isOpen: true, selectedClient: audience, mode: 'update_customer_segments' });
  };

  const handleDeleteAudienceClick = (audience) => {
    setAudienceModalState({ isOpen: true, selectedClient: audience, mode: 'delete_audience' });
  };

  // Add Tenant Specific Columns to Columns
  useEffect(() => {
    const tenantColumns = tenantSpecificColumns[tenant];
    
    if(tenantColumns && tenantColumns.length > 0){
      tenantColumns.forEach((column) => {
        setColumns((prevColumns) => {
          if(!prevColumns.find((col) => col.id === column.id)){
            return [...prevColumns.slice(0, -2), column, ...prevColumns.slice(-2)]
          }else{
            return prevColumns
          }
        });
      })
    }
  }, [tenant])

  return (
    <div>
      <Box
        sx={{ 
          mb: 2, 
          display: { xs: 'none', sm: 'flex' },
          flexWrap: 'wrap', 
          gap: 1 
        }}
      >
        {/* All Filters */}
        <Tooltip title="Search by Name, Phone Number, Email" placement="top" arrow>
          <TextField
            size="small"
            placeholder="Search"
            autoComplete='off'
            value={searchQuery}
            onChange={(e) => updateSearchValue(e.target.value)}
            sx={{ width: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Typography variant="caption">{charCount}/50</Typography>
                </InputAdornment>
              ),
            }}
          />
        </Tooltip>

        {/* Segment Filter */}
        <Button 
          variant="outlined" 
          onClick={handleSegmentClick}
          startIcon={<PeopleIcon />}
          endIcon={<Box component="span">▼</Box>}
          sx={{ textTransform: 'none', borderRadius: 1, height: '40px' }}
        >
          Segments
          {segmentFilter && (
            <Chip 
              label={segmentFilter} 
              size="small" 
              onDelete={() => setSegmentFilter('')}
              sx={{ ml: 1 }}
            />
          )}
        </Button>
        <Menu
          anchorEl={segmentAnchorEl}
          open={Boolean(segmentAnchorEl)}
          onClose={() => setSegmentAnchorEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            style: {
              maxWidth: segmentAnchorEl ? segmentAnchorEl.offsetWidth : undefined,
              width: 'auto',
              maxHeight: '300px',
              overflowY: 'auto'
            },
          }}
        >
          {allSegments.length === 0 ? (
            <MenuItem disabled>No segments found</MenuItem>
          ) : (
            allSegments.map((segment) => (
              <MenuItem key={segment} onClick={() => handleSegmentSelect(segment)}>
                {segment}
                {segmentFilter === segment && <CheckIcon sx={{ ml: 1 }} color="success" />}
              </MenuItem>
            ))
          )}
        </Menu>

        {/* Gender Filter */}
        <Button 
          variant="outlined" 
          onClick={handleGenderClick}
          startIcon={<PeopleIcon />}
          endIcon={<Box component="span">▼</Box>}
          sx={{ textTransform: 'none', borderRadius: 1, height: '40px' }}
        >
          Gender
          {genderFilter && (
            <Chip 
              label={genderFilter} 
              size="small" 
              onDelete={() => setGenderFilter('')}
              sx={{ ml: 1 }}
            />
          )}
        </Button>
        <Menu
          anchorEl={genderAnchorEl}
          open={Boolean(genderAnchorEl)}
          onClose={() => setGenderAnchorEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            style: {
              maxWidth: genderAnchorEl ? genderAnchorEl.offsetWidth : undefined,
              width: 'auto',
              maxHeight: '200px',
              overflowY: 'auto'
            },
          }}
        >
          {['Male', 'Female', 'Null'].map((gender) => (
            <MenuItem key={gender} onClick={() => handleGenderSelect(gender)}>
              {gender}
              {genderFilter === gender && <CheckIcon sx={{ ml: 1 }} color="success" />}
            </MenuItem>
          ))}
        </Menu>

        {/* Age Filter */}
        <Button 
          variant="outlined" 
          onClick={handleAgeClick}
          startIcon={<PeopleIcon />}
          endIcon={<Box component="span">▼</Box>}
          sx={{ textTransform: 'none', borderRadius: 1, height: '40px' }}
        >
          Age
          {ageFilter && (
            <Chip 
              label={ageFilter} 
              size="small" 
              onDelete={() => setAgeFilter('')}
              sx={{ ml: 1 }}
            />
          )}
        </Button>
        <Menu
          anchorEl={ageAnchorEl}
          open={Boolean(ageAnchorEl)}
          onClose={() => setAgeAnchorEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            style: {
              maxWidth: ageAnchorEl ? ageAnchorEl.offsetWidth : undefined,
              width: 'auto',
              maxHeight: '300px',
              overflowY: 'auto'
            },
          }}
        >
          {allAgeOptions.length === 0 ? (
            <MenuItem disabled>No age options found</MenuItem>
          ) : (
            allAgeOptions.map((age) => (
              <MenuItem key={age} onClick={() => handleAgeSelect(age)}>
                {age}
                {ageFilter === age && <CheckIcon sx={{ ml: 1 }} color="success" />}
              </MenuItem>
            ))
          )}
        </Menu>

        {/* City Filter */}
        <Button 
          variant="outlined" 
          onClick={handleCityClick}
          startIcon={<PeopleIcon />}
          endIcon={<Box component="span">▼</Box>}
          sx={{ textTransform: 'none', borderRadius: 1, height: '40px' }}
        >
          City
          {cityFilter && (
            <Chip 
              label={cityFilter} 
              size="small" 
              onDelete={() => setCityFilter('')}
              sx={{ ml: 1 }}
            />
          )}
        </Button>
        <Menu
          anchorEl={cityAnchorEl}
          open={Boolean(cityAnchorEl)}
          onClose={() => setCityAnchorEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            style: {
              minWidth: cityAnchorEl ? cityAnchorEl.offsetWidth : undefined,
              width: 'auto', // Allow width to adjust based on content
              maxHeight: '300px',
              overflowY: 'auto'
            },
          }}
        >
          {allCityOptions.length === 0 ? (
            <MenuItem disabled>No city options found</MenuItem>
          ) : (
            allCityOptions.map((city) => (
              <MenuItem key={city} onClick={() => handleCitySelect(city)}>
                {city}
                {cityFilter === city && <CheckIcon sx={{ ml: 1 }} color="success" />}
              </MenuItem>
            ))
          )}
        </Menu>

        {/* Reset button */}
        <Button
          variant="outlined"
          onClick={handleReset}
          sx={{ height: '40px' }}
        >
          Reset
        </Button>

        {/* Actions Button - with ml: 'auto' to push it to the right */}
        <Button
          variant="outlined"
          onClick={handleActionsClick}
          endIcon={<Box component="span">▼</Box>}
          sx={{ 
            height: '40px',
            textTransform: 'none',
            ml: 'auto' // This pushes the button to the rightmost position
          }}
        >
          Actions
        </Button>
        <Menu
          anchorEl={actionsAnchorEl}
          open={Boolean(actionsAnchorEl)}
          onClose={handleActionsClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            style: {
              minWidth: '160px',
            },
          }}
        >
          <MenuItem onClick={handleExportFromMenu}>
            <DownloadIcon sx={{ mr: 1 }} />
            Export Data
          </MenuItem>
          
          {hasAddPermission && (
            <MenuItem onClick={handleUploadFromMenu}>
              <CloudUploadIcon sx={{ mr: 1 }} />
              Upload File
            </MenuItem>
          )}
          
          {hasAddPermission && (
            <MenuItem 
              onClick={handleEditSegmentFromMenu}
              disabled={selectedRecords.length === 0}
              sx={{
                '&.Mui-disabled': {
                  opacity: 0.5
                }
              }}
            >
              <CreateIcon sx={{ mr: 1 }} />
              Segment
            </MenuItem>
          )}
        </Menu>
      </Box>
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 600 }}> {/* Changed from 530 to 450 to match BroadcastCampaign */}
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.id === 'select' ? 'center' : "left"}
                    style={{ 
                      minWidth: column.minWidth,
                      // Removed border styling to match BroadcastCampaign clean look
                      backgroundColor: column.id === 'select' ? '#1976d2' : undefined,
                      color: column.id === 'select' ? 'white' : undefined
                    }}
                  >
                    {column.id === 'select' ? (
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <Checkbox
                          indeterminate={selectedRecords.length > 0 && selectedRecords.length < audienceData.length}
                          checked={audienceData.length > 0 && selectedRecords.length === audienceData.length}
                          onChange={handleSelectAll}
                          sx={{
                            padding: '8px',
                            '& .MuiSvgIcon-root': {
                              visibility: 'visible !important',
                              opacity: 1,
                              fill: 'white',
                            }
                          }}
                        />
                      </div>
                    ) : (
                      // Changed from <h3> to regular text to match BroadcastCampaign
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {audienceData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    No match found
                  </TableCell>
                </TableRow>
              ) : (
                audienceData.map((audience, index) => {
                  const isSelected = isRecordSelected(audience.id);
                  return (
                    <TableRow 
                      key={index}
                      hover
                      role="checkbox"
                      aria-checked={isSelected}
                      tabIndex={-1}
                      selected={isSelected}
                    >
                      <TableCell align="center">
                        {/* Removed border styling */}
                        <Checkbox
                          checked={isSelected}
                          onChange={(event) => handleSelectRecord(event, audience.id)}
                        />
                      </TableCell>
                      <TableCell align="left">
                        {/* Removed border styling */}
                        {`${audience.first_name || ''} ${audience.last_name || ''}`.trim() || 'N/A'}
                      </TableCell>
                      <TableCell align="left">
                        {/* Removed border styling */}
                        (+{audience.country_code}) {audience.phone_no}
                      </TableCell>
                      <TableCell align="left">
                        {audience.age}
                      </TableCell>
                      <TableCell align="left">
                        {audience.gender || 'Null'}
                      </TableCell>
                      <TableCell align="left">
                        {audience.city || 'N/A'}
                      </TableCell>
                      <TableCell align="left">
                        {/* Removed border styling */}
                        {audience.segments ? 
                          audience.segments.join(', ') :
                            "Unknown"
                        }
                      </TableCell>
                      <TableCell align="left">
                        {/* Removed border styling */}
                        {audience.email}
                      </TableCell>
                      {tenantSpecificColumns[tenant] && tenantSpecificColumns[tenant].length > 0 &&
                       tenantSpecificColumns[tenant].map(tenantSpecificColumn => 
                        <TableCell align="left" key={tenantSpecificColumn.id}>{audience[tenantSpecificColumn.id]}</TableCell>)
                      }
                      <TableCell align="left">
                        {audience.opted_in ? (
                          <Chip
                            variant="outlined"
                            color="success"
                            label="Yes"
                            size="small"
                            icon={<CheckCircleIcon />}
                          />
                        ) : (
                          <Chip
                            variant="outlined"
                            color="error"
                            label="No"
                            size="small"
                            icon={<CancelIcon />}
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {/* Removed border styling */}
                        <Tooltip title="Delete audience" placement="top" arrow>
                          <IconButton
                            variant="contained" 
                            onClick={() => handleDeleteAudienceClick(audience)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[20]}
          component="div"
          count={rowCount}
          rowsPerPage={controller.rowsPerPage}
          page={controller.page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
        <AudienceFormDialog
          modalState={audienceModalState}
          handleModalState={setAudienceModalState}
          selectedClient={audienceModalState.selectedClient}
          allSegments={allSegments}
          activeSegments={activeSegments}
          allSegmentsDetails={allSegmentsDetails}
          allActiveSegmentsDetails={allActiveSegmentsDetails}
          allZonesDetails={allZonesDetails}
          fetchData={fetchData}
          selectedRecords={selectedRecords}
          audienceData={audienceData}
        />
        <AudienceFileFormDialog
          modalState={audienceFileModalState}
          handleModalState={setAudienceFileModalState }
        />
      </Paper>
      
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Upload File</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" gap={2} p={2} width="100%">
          <Typography variant="body2" component="span">
            Click{" "}
            <Typography
              variant="body2"
              component="a"
              href={sampleFileURL}
              download="sample.csv"
              style={{ textDecoration: "none", cursor: "pointer", color: "blue" }}
            >
              here
            </Typography>{" "}
            to download sample file
          </Typography>
            {/* Hidden File Input */}
            <input
              type="file"
              accept="text/csv,application/vnd.ms-excel"
              onChange={handleFileChange}
              style={{ display: "none" }}
              id="file-input"
            />
            <label htmlFor="file-input">
              <Button variant="contained" component="span" startIcon={<CloudUploadIcon />} disabled={loading}>
                Select File
              </Button>
            </label>

            {/* Display Selected File */}
            {file && (
              <Box textAlign="center">
                <Typography variant="subtitle1">Selected File:</Typography>
                <Typography variant="body2">{file.name}</Typography>
              </Box>
            )}

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ width: "100%" }}>
                <Typography variant="body1" fontWeight="bold">
                  {error.summary}
                </Typography>
                {error.details.length > 0 && (
                  <ul style={{ margin: 0, paddingLeft: "20px" }}>
                    {(showAllErrors ? error.details : error.details.slice(0, 5)).map((errMsg, index) => (
                      <li key={index}>{errMsg}</li>
                    ))}
                  </ul>
                )}
                {error.details.length > 5 && (
                  <Typography
                    variant="body2"
                    sx={{ cursor: "pointer", color: "blue", textDecoration: "underline", mt: 1 }}
                    onClick={() => setShowAllErrors(!showAllErrors)}
                  >
                    {showAllErrors ? "Show less..." : "Read more..."}
                  </Typography>
                )}
              </Alert>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleUpload} variant="contained" color="primary" disabled={!file || loading}>
            {loading ? <CircularProgress size={24} /> : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
